import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Check,
  Loader2,
  AlertCircle,
  Key,
  Globe,
  Mail,
  Zap,
  Copy,
  Shield,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Circle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { key: 'credentials', label: 'API Credentials', icon: Key },
  { key: 'domain', label: 'Domain Auth', icon: Globe },
  { key: 'webhooks', label: 'Webhooks', icon: Zap },
  { key: 'activate', label: 'Activate', icon: Check },
];

const SendGridSetupWizard = ({ open, onOpenChange, onComplete }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null);

  // Step 1: Credentials
  const [apiKey, setApiKey] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [replyToDomain, setReplyToDomain] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);

  // Step 2: Domain Auth
  const [domainInput, setDomainInput] = useState('');
  const [initDomain, setInitDomain] = useState(false);
  const [verifyDomain, setVerifyDomain] = useState(false);

  // Step 3: Webhooks
  const [settingUpParse, setSettingUpParse] = useState(false);
  const [settingUpEvents, setSettingUpEvents] = useState(false);

  // Step 4: Toggle
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (open) fetchSetupStatus();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSetupStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings/sendgrid-setup`, { withCredentials: true });
      setSetup(res.data);
      // Pre-fill from current config
      if (res.data.steps.credentials.sender_email) {
        setSenderEmail(res.data.steps.credentials.sender_email);
      }
      if (res.data.steps.inbound_parse?.reply_to_domain) {
        setReplyToDomain(res.data.steps.inbound_parse.reply_to_domain);
      }
    } catch (err) {
      console.error('Failed to load setup status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!apiKey.trim() && setup?.steps?.credentials?.api_key_set) {
      // Key already set, just update sender/reply-to
    } else if (!apiKey.trim()) {
      toast.error('Please enter your SendGrid API key');
      return;
    }
    if (!senderEmail.trim()) {
      toast.error('Please enter a verified sender email');
      return;
    }
    setSavingCreds(true);
    try {
      const payload = { sender_email: senderEmail.trim(), reply_to_domain: replyToDomain.trim() };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      else {
        // Re-send existing key placeholder — backend will keep existing
        toast.error('API key is required');
        setSavingCreds(false);
        return;
      }
      await axios.post(`${API}/settings/sendgrid-setup/credentials`, payload, { withCredentials: true });
      toast.success('Credentials saved & validated!');
      await fetchSetupStatus();
      setStep(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSavingCreds(false);
    }
  };

  const handleInitDomain = async () => {
    if (!domainInput.trim()) {
      toast.error('Enter your domain');
      return;
    }
    setInitDomain(true);
    try {
      await axios.post(`${API}/settings/domain-auth`, { domain: domainInput.trim() }, { withCredentials: true });
      toast.success('Domain authentication initiated! Add the DNS records shown below.');
      await fetchSetupStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate domain auth');
    } finally {
      setInitDomain(false);
    }
  };

  const handleVerifyDomain = async () => {
    setVerifyDomain(true);
    try {
      const res = await axios.post(`${API}/settings/domain-auth/verify`, {}, { withCredentials: true });
      if (res.data.valid) {
        toast.success('Domain verified!');
        await fetchSetupStatus();
      } else {
        toast.error('DNS records not yet verified. Allow up to 48 hours for propagation.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifyDomain(false);
    }
  };

  const handleSetupInboundParse = async () => {
    setSettingUpParse(true);
    try {
      const hostname = replyToDomain || `replies.${domainInput || 'yourdomain.com'}`;
      const webhookUrl = `${BACKEND_URL}/api/webhooks/sendgrid/inbound`;
      await axios.post(`${API}/settings/inbound-parse`, { hostname, webhook_url: webhookUrl }, { withCredentials: true });
      toast.success('Inbound Parse configured!');
      await fetchSetupStatus();
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('already exists')) {
        toast.info('Inbound Parse already configured');
        await fetchSetupStatus();
      } else {
        toast.error(detail || 'Failed to setup Inbound Parse');
      }
    } finally {
      setSettingUpParse(false);
    }
  };

  const handleSetupEventWebhook = async () => {
    setSettingUpEvents(true);
    try {
      await axios.post(`${API}/settings/sendgrid-setup/event-webhook`, {}, { withCredentials: true });
      toast.success('Event Webhook configured!');
      await fetchSetupStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to setup Event Webhook');
    } finally {
      setSettingUpEvents(false);
    }
  };

  const handleToggleProvider = async (provider) => {
    setToggling(true);
    try {
      await axios.post(`${API}/settings/sendgrid-setup/toggle-provider`, { provider }, { withCredentials: true });
      toast.success(`Email provider switched to ${provider === 'sendgrid' ? 'SendGrid' : 'Gmail'}`);
      await fetchSetupStatus();
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to switch provider');
    } finally {
      setToggling(false);
    }
  };

  const stepComplete = (idx) => {
    if (!setup) return false;
    const s = setup.steps;
    switch (idx) {
      case 0: return s.credentials.complete;
      case 1: return s.domain_auth.complete;
      case 2: return s.inbound_parse.complete && s.event_webhook.complete;
      case 3: return setup.email_provider === 'sendgrid';
      default: return false;
    }
  };

  const domainAuth = setup?.steps?.domain_auth?.domain;
  const dnsRecords = domainAuth?.dns_records || [];
  const parseSettings = setup?.steps?.inbound_parse?.settings || [];
  const eventWebhook = setup?.steps?.event_webhook?.settings;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sendgrid-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">SendGrid Setup</DialogTitle>
          <DialogDescription>Configure SendGrid for outbound email, reply tracking, and analytics.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Step indicator */}
            <div className="flex items-center gap-1 mb-4">
              {STEPS.map((s, idx) => (
                <React.Fragment key={s.key}>
                  <button
                    onClick={() => setStep(idx)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      idx === step ? 'bg-forest text-white' : stepComplete(idx) ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-muted-foreground'
                    }`}
                    data-testid={`wizard-step-${s.key}`}
                  >
                    {stepComplete(idx) ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                    {s.label}
                  </button>
                  {idx < STEPS.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />}
                </React.Fragment>
              ))}
            </div>

            <Separator />

            {/* Step 1: Credentials */}
            {step === 0 && (
              <div className="space-y-4 py-4" data-testid="wizard-credentials">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <Key className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">SendGrid API Key</p>
                    <p className="text-xs text-blue-700 mt-1">
                      Get your API key from{' '}
                      <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="underline font-medium">
                        SendGrid Dashboard &rarr; Settings &rarr; API Keys
                      </a>. Requires <strong>Full Access</strong> permissions.
                    </p>
                  </div>
                </div>

                <div>
                  <Label>API Key {setup?.steps?.credentials?.api_key_set && <Badge className="bg-green-600 ml-2 text-[10px]">Set</Badge>}</Label>
                  <Input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={setup?.steps?.credentials?.api_key_set ? '••••••••••••••••••' : 'SG.xxxxxxxxxx...'}
                    className="font-mono mt-1"
                    data-testid="input-api-key"
                  />
                </div>
                <div>
                  <Label>Verified Sender Email</Label>
                  <Input
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                    placeholder="noreply@yourdomain.com"
                    className="mt-1"
                    data-testid="input-sender-email"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Must be verified in SendGrid &rarr; Sender Authentication</p>
                </div>
                <div>
                  <Label>Reply-To Domain (optional)</Label>
                  <Input
                    value={replyToDomain}
                    onChange={(e) => setReplyToDomain(e.target.value)}
                    placeholder="replies.yourdomain.com"
                    className="mt-1"
                    data-testid="input-reply-domain"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Subdomain for receiving reply emails. Requires MX record setup later.</p>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button onClick={handleSaveCredentials} disabled={savingCreds} className="bg-forest hover:bg-forest-dark text-white" data-testid="save-credentials-btn">
                    {savingCreds ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Validating...</> : <>Save & Continue<ChevronRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Domain Authentication */}
            {step === 1 && (
              <div className="space-y-4 py-4" data-testid="wizard-domain">
                {!setup?.steps?.credentials?.complete && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-sm text-amber-800 flex items-center gap-2"><AlertCircle className="h-4 w-4" />Complete Step 1 first.</p>
                  </div>
                )}

                {domainAuth?.valid ? (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="h-5 w-5 text-green-600" />
                      <span className="font-medium text-green-800">{domainAuth.domain}</span>
                      <Badge className="bg-green-600">Verified</Badge>
                    </div>
                    <p className="text-xs text-green-700">Domain authentication is complete. SPF, DKIM are configured.</p>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Input
                        placeholder="yourdomain.com"
                        value={domainInput}
                        onChange={(e) => setDomainInput(e.target.value)}
                        data-testid="input-domain"
                      />
                      <Button onClick={handleInitDomain} disabled={initDomain || !setup?.steps?.credentials?.complete} className="bg-forest hover:bg-forest-dark text-white shrink-0">
                        {initDomain ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Authenticate'}
                      </Button>
                    </div>
                  </>
                )}

                {dnsRecords.length > 0 && !domainAuth?.valid && (
                  <div className="border border-stone-200 rounded-lg overflow-hidden">
                    <div className="bg-stone-50 px-4 py-2 border-b">
                      <p className="text-xs font-medium text-muted-foreground uppercase">Add these DNS records</p>
                    </div>
                    <div className="divide-y divide-stone-100">
                      {dnsRecords.map((r) => (
                        <div key={r.host || r.name} className="px-4 py-2.5">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-[10px] font-mono">{(r.type || 'CNAME').toUpperCase()}</Badge>
                            <span className={`text-xs ${r.valid ? 'text-green-600' : 'text-amber-600'}`}>{r.valid ? 'Valid' : 'Pending'}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs">
                            <span className="text-muted-foreground w-10">Host:</span>
                            <code className="bg-stone-100 px-1 rounded font-mono break-all">{r.host}</code>
                            <button onClick={() => { navigator.clipboard.writeText(r.host); toast.success('Copied!'); }}><Copy className="h-3 w-3 text-muted-foreground" /></button>
                          </div>
                          <div className="flex items-center gap-1 text-xs mt-0.5">
                            <span className="text-muted-foreground w-10">Value:</span>
                            <code className="bg-stone-100 px-1 rounded font-mono break-all">{r.data}</code>
                            <button onClick={() => { navigator.clipboard.writeText(r.data); toast.success('Copied!'); }}><Copy className="h-3 w-3 text-muted-foreground" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {dnsRecords.length > 0 && !domainAuth?.valid && (
                  <Button onClick={handleVerifyDomain} disabled={verifyDomain} className="w-full bg-forest hover:bg-forest-dark text-white" data-testid="verify-domain-wizard-btn">
                    {verifyDomain ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Verifying...</> : <><Shield className="mr-2 h-4 w-4" />Verify Domain</>}
                  </Button>
                )}

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(0)}><ChevronLeft className="mr-2 h-4 w-4" />Back</Button>
                  <Button onClick={() => setStep(2)} disabled={!setup?.steps?.domain_auth?.complete} className="bg-forest hover:bg-forest-dark text-white">
                    Continue<ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Webhooks */}
            {step === 2 && (
              <div className="space-y-4 py-4" data-testid="wizard-webhooks">
                {/* Inbound Parse */}
                <div className="p-4 rounded-lg border border-stone-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-forest" />
                      <span className="font-medium text-sm">Inbound Parse (Reply Webhook)</span>
                    </div>
                    {setup?.steps?.inbound_parse?.complete ? (
                      <Badge className="bg-green-600">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Not configured</Badge>
                    )}
                  </div>
                  {setup?.steps?.inbound_parse?.complete ? (
                    <p className="text-xs text-muted-foreground">Replies to <strong>*@{replyToDomain || setup?.steps?.inbound_parse?.reply_to_domain}</strong> will be forwarded for AI classification.</p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground mb-2">
                        Receives reply emails via webhook. Requires MX record: <code className="bg-stone-100 px-1 rounded">{replyToDomain || 'replies.yourdomain.com'}</code> &rarr; <code className="bg-stone-100 px-1 rounded">mx.sendgrid.net</code> (priority 10)
                      </p>
                      <Button onClick={handleSetupInboundParse} disabled={settingUpParse || !setup?.steps?.credentials?.complete} size="sm" className="bg-forest hover:bg-forest-dark text-white" data-testid="setup-parse-wizard-btn">
                        {settingUpParse ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate Inbound Parse'}
                      </Button>
                    </>
                  )}
                </div>

                {/* Event Webhook */}
                <div className="p-4 rounded-lg border border-stone-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-forest" />
                      <span className="font-medium text-sm">Event Webhook (Open/Click/Bounce)</span>
                    </div>
                    {setup?.steps?.event_webhook?.complete ? (
                      <Badge className="bg-green-600">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Not configured</Badge>
                    )}
                  </div>
                  {setup?.steps?.event_webhook?.complete ? (
                    <p className="text-xs text-muted-foreground">Tracking email opens, clicks, and bounces.</p>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground mb-2">Enables tracking when leads open emails, click links, or emails bounce. No DNS needed.</p>
                      <Button onClick={handleSetupEventWebhook} disabled={settingUpEvents || !setup?.steps?.credentials?.complete} size="sm" className="bg-forest hover:bg-forest-dark text-white" data-testid="setup-events-wizard-btn">
                        {settingUpEvents ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Activate Event Tracking'}
                      </Button>
                    </>
                  )}
                </div>

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(1)}><ChevronLeft className="mr-2 h-4 w-4" />Back</Button>
                  <Button onClick={() => setStep(3)} className="bg-forest hover:bg-forest-dark text-white">Continue<ChevronRight className="ml-2 h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* Step 4: Activate */}
            {step === 3 && (
              <div className="space-y-4 py-4" data-testid="wizard-activate">
                <div className="space-y-2">
                  {STEPS.slice(0, 3).map((s, idx) => (
                    <div key={s.key} className={`flex items-center gap-3 p-3 rounded-lg border ${stepComplete(idx) ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                      {stepComplete(idx) ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <AlertCircle className="h-5 w-5 text-amber-600" />}
                      <span className={`text-sm font-medium ${stepComplete(idx) ? 'text-green-800' : 'text-amber-800'}`}>{s.label}</span>
                      <Badge className={`ml-auto ${stepComplete(idx) ? 'bg-green-600' : 'bg-amber-500'}`}>{stepComplete(idx) ? 'Complete' : 'Pending'}</Badge>
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="p-4 rounded-lg border-2 border-forest/20 bg-sage/10">
                  <p className="font-medium text-sm mb-2">Email Provider</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    {setup?.email_provider === 'sendgrid'
                      ? 'SendGrid is your active email provider. New outreach emails are sent via SendGrid.'
                      : 'Gmail is currently active. Switch to SendGrid to use the configured setup.'}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleToggleProvider('sendgrid')}
                      disabled={toggling || setup?.email_provider === 'sendgrid' || !setup?.all_complete}
                      className={`flex-1 ${setup?.email_provider === 'sendgrid' ? 'bg-green-600 hover:bg-green-700' : 'bg-forest hover:bg-forest-dark'} text-white`}
                      data-testid="activate-sendgrid-btn"
                    >
                      {toggling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : setup?.email_provider === 'sendgrid' ? <><Check className="mr-2 h-4 w-4" />SendGrid Active</> : 'Activate SendGrid'}
                    </Button>
                    <Button
                      onClick={() => handleToggleProvider('gmail')}
                      disabled={toggling || setup?.email_provider === 'gmail'}
                      variant="outline"
                      className="flex-1"
                      data-testid="activate-gmail-btn"
                    >
                      {setup?.email_provider === 'gmail' ? <><Check className="mr-2 h-4 w-4" />Gmail Active</> : 'Switch to Gmail'}
                    </Button>
                  </div>
                  {!setup?.all_complete && setup?.email_provider !== 'sendgrid' && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1"><AlertCircle className="h-3 w-3" />Complete all steps above before activating SendGrid.</p>
                  )}
                </div>

                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={() => setStep(2)}><ChevronLeft className="mr-2 h-4 w-4" />Back</Button>
                  <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendGridSetupWizard;
